import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { MainLayout } from "@/components/layout/MainLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuthStore } from "@/stores/useAuthStore";

// Auth pages
import { Login } from "@/pages/auth/Login";

// Dashboard and existing pages
import { Dashboard } from "@/pages/Dashboard";
import { PermissionRegistry } from "@/pages/PermissionRegistry";
import { AccessDebugger } from "@/pages/AccessDebugger";
import { PackageManager } from "@/pages/PackageManager";
import { OrgBootstrapper } from "@/pages/OrgBootstrapper";
import { DependencyGraph } from "@/pages/DependencyGraph";
import { PermissionMatrix } from "@/pages/PermissionMatrix";

// New pages
import { OrganizationList } from "@/pages/organizations/OrganizationList";
import { OrganizationDetail } from "@/pages/organizations/OrganizationDetail";
import { OrganizationCreate } from "@/pages/organizations/OrganizationCreate";
import { SystemHealth } from "@/pages/system/SystemHealth";
import { JobMonitor } from "@/pages/system/JobMonitor";
import { AnalyticsDashboard } from "@/pages/analytics/AnalyticsDashboard";
import { AuditLog } from "@/pages/audit/AuditLog";
import { UserList } from "@/pages/users/UserList";
import { PlanList } from "@/pages/plans/PlanList";
import { PlanEditor } from "@/pages/plans/PlanEditor";
import { SubscriptionList } from "@/pages/subscriptions/SubscriptionList";
import { InvoiceList } from "@/pages/invoices/InvoiceList";
import { CouponList } from "@/pages/coupons/CouponList";
import { MenuBuilder } from "@/pages/MenuBuilder";
import { UserPerspective } from "@/pages/UserPerspective";
import { Settings } from "@/pages/Settings";

// Developer Tools pages
import { ScoringCalculator } from "@/pages/developer/ScoringCalculator";
import { KPICalculator } from "@/pages/developer/KPICalculator";
import { GamificationTester } from "@/pages/developer/GamificationTester";
import { URLDetector } from "@/pages/developer/URLDetector";
import { AppClassifier } from "@/pages/developer/AppClassifier";
import { FullSimulation } from "@/pages/developer/FullSimulation";
import { DataCleanup } from "@/pages/developer/DataCleanup";
import { ImportExport } from "@/pages/ImportExport";
import { TestLabPage } from "@/pages/developer/TestLabPage";
import { RecalculationTool } from "@/pages/developer/RecalculationTool";
import { PipelineValidatorPage } from "@/pages/developer/PipelineValidatorPage";
import { EmailPlayground } from "@/pages/developer/EmailPlayground";

function App() {
	const { initialize, isLoading } = useAuthStore();

	// Initialize auth state on app mount
	useEffect(() => {
		initialize();
	}, [initialize]);

	// Show loading screen while initializing auth
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-100">
				<div className="flex flex-col items-center gap-4">
					<div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600" />
					<p className="text-gray-600">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<Routes>
				{/* Public routes */}
				<Route path="/login" element={<Login />} />

				{/* Protected routes - require authentication */}
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<MainLayout />
						</ProtectedRoute>
					}
				>
					<Route index element={<Navigate to="/dashboard" replace />} />

					{/* Overview */}
					<Route path="dashboard" element={<Dashboard />} />

					{/* Management */}
					<Route path="organizations" element={<OrganizationList />} />
					<Route path="organizations/new" element={<OrganizationCreate />} />
					<Route path="organizations/:id" element={<OrganizationDetail />} />
					<Route path="plans" element={<PlanList />} />
					<Route path="plans/:id" element={<PlanEditor />} />
					<Route path="packages" element={<PackageManager />} />
					<Route path="subscriptions" element={<SubscriptionList />} />
					<Route path="invoices" element={<InvoiceList />} />
					<Route path="coupons" element={<CouponList />} />
					<Route path="users" element={<UserList />} />

					{/* System */}
					<Route path="system/health" element={<SystemHealth />} />
					<Route path="system/job-monitor" element={<JobMonitor />} />
					<Route path="analytics" element={<AnalyticsDashboard />} />
					<Route path="audit-log" element={<AuditLog />} />
					<Route path="settings" element={<Settings />} />

					{/* Authorization */}
					<Route path="permissions" element={<PermissionRegistry />} />
					<Route path="debugger" element={<AccessDebugger />} />
					<Route path="bootstrap" element={<OrgBootstrapper />} />
					<Route path="dependencies" element={<DependencyGraph />} />
					<Route path="matrix" element={<PermissionMatrix />} />
					<Route path="menu-builder" element={<MenuBuilder />} />
					<Route path="user-perspective" element={<UserPerspective />} />

					{/* Developer Tools */}
					<Route path="dev/scoring" element={<ScoringCalculator />} />
					<Route path="dev/kpi" element={<KPICalculator />} />
					<Route path="dev/gamification" element={<GamificationTester />} />
					<Route path="dev/url" element={<URLDetector />} />
					<Route path="dev/classification" element={<AppClassifier />} />
					<Route path="dev/simulate" element={<FullSimulation />} />
					<Route path="dev/cleanup" element={<DataCleanup />} />
					<Route path="dev/import-export" element={<ImportExport />} />
					<Route path="dev/test-lab" element={<TestLabPage />} />
					<Route path="dev/pipeline-validator" element={<PipelineValidatorPage />} />
					<Route path="dev/recalculate" element={<RecalculationTool />} />
					<Route path="dev/email-playground" element={<EmailPlayground />} />
				</Route>

				{/* Catch all - redirect to dashboard */}
				<Route path="*" element={<Navigate to="/dashboard" replace />} />
			</Routes>
			<Toaster />
		</>
	);
}

export default App;
